import React from 'react';
import Callout from 'src/components/InformationCallout/Callout';
import LinkPreviewCard from 'src/components/LinkPreviewCard/LinkPreviewCard';
import {
  KEY_PICKUP_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import calcomBookingDashboardPreview from '../../assets/calcom-booking-dashboard-preview.jpg';
import './calcom-event-explainer.scss';

const CALCOM_BOOKING_DASHBOARD_DOC =
  'https://cal.com/blog/a-complete-walkthrough-of-cal-com-s-booking-dashboard-its-key-features';

interface ICalcomEventExplainerCopy {
  title: string;
  subtitle: React.ReactNode;
  detail: React.ReactNode;
}

const resolveExplainer = (
  juniperEventType: number
): ICalcomEventExplainerCopy => {
  if (juniperEventType === KEY_PICKUP_EVENT_TYPE) {
    return {
      title: 'How this key pickup was scheduled',
      subtitle: (
        <>
          Key pickups are a Juniper feature, scheduled through Cal.com. Juniper
          emailed this homeowner a branded &ldquo;Book your key pickup&rdquo;
          link — they picked a time from the live availability of the admin or
          property developer who set it up.
        </>
      ),
      detail: (
        <>
          The booking lives in Cal.com, not Juniper — that&apos;s where it&apos;s
          rescheduled or cancelled. Juniper surfaces it here so your whole
          schedule stays in one place.
        </>
      ),
    };
  }

  if (juniperEventType === WALKTHROUGH_EVENT_TYPE) {
    return {
      title: 'How this walkthrough was scheduled',
      subtitle: (
        <>
          This walkthrough was booked through Cal.com against an admin or property
          developer&apos;s availability, not scheduled directly in Juniper. The
          attendee picked a slot from the times they were free.
        </>
      ),
      detail: (
        <>
          The booking is owned in Cal.com, so reschedules and cancellations
          happen there and flow back automatically. Juniper mirrors it here so
          you see it alongside everything else.
        </>
      ),
    };
  }

  return {
    title: 'How this booking was scheduled',
    subtitle: (
      <>
        This event was booked through Cal.com against your connected
        availability, rather than scheduled directly in Juniper.
      </>
    ),
    detail: (
      <>
        The booking is owned in Cal.com — that&apos;s where it&apos;s rescheduled
        or cancelled — and Juniper mirrors it here so your whole schedule stays
        in one place.
      </>
    ),
  };
};

interface CalcomEventExplainerProps {
  juniperEventType: number;
}

const CalcomEventExplainer = ({
  juniperEventType,
}: CalcomEventExplainerProps) => {
  const explainer: ICalcomEventExplainerCopy =
    resolveExplainer(juniperEventType);

  return (
    <Callout variant="info" title={explainer.title} subtitle={explainer.subtitle}>
      <p className="calcom-event-explainer-detail">{explainer.detail}</p>

      <LinkPreviewCard
        href={CALCOM_BOOKING_DASHBOARD_DOC}
        title="Where Cal.com bookings are managed"
        description="How attendees book, and where to view, reschedule, or cancel in Cal.com."
        siteName="Cal.com"
        image={calcomBookingDashboardPreview}
      />
    </Callout>
  );
};

export default CalcomEventExplainer;